import React, { useState } from 'react';
import {
  withStyles,
  Modal,
} from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  setScissorLiftValues,
  deleteScissoLiftItem,
  searchScissorLiftList,
  restoreOriginalScissorLift,
  createScissorLiftItem,
  updateScissorLiftList,
} from '~/store/dailyparts/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate, formatTime, newMomentDate, parseDate, parseDateTimeToTimestamp, subtractDaysFromNow,
} from '~/utils/moment';
import EditHistoryButton from '~/components/EditHistoryButton';
import { usePagination } from '../hooks';

const SCISSOR_LIFT_EQUIP_TYPES = [24];

const ScissorLiftList = () => {
  const { t: translate } = useTranslation();

  const scissorLiftList = useSelector(state => state.dailyParts.scissorLiftList);
  const filters = useSelector(state => state.dailyParts.filters);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const scissorLiftGroups = useSelector(
    state => state.manager.equipmentsGroups
      .filter(r => SCISSOR_LIFT_EQUIP_TYPES.includes(r.id_equipament)), shallowEqual,
  );
  const scissorLiftEquips = useSelector(
    state => state.manager.equipments
      .filter(r => SCISSOR_LIFT_EQUIP_TYPES.includes(r.id_equip)), shallowEqual,
  );
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators.filter(o => o.name), shallowEqual);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const operationTypes = useSelector(state => state.manager.operationTypes);
  const operationTypeActivities = useSelector(state => state.manager.operationTypeActivities);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const originalScissorLiftList = useSelector(state => state.dailyParts.originalScissorLiftList);

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);
  const scissorLiftPagination = useSelector(
    state => state.dailyParts.pagination.scissor_liftPagination,
  );

  const dispatch = useDispatch();

  const isDateEditable = (rowData, key, t, userLimit, enableClosureFlag, closureList) => {
    let userLimitEdit = subtractDaysFromNow(userLimit);

    if (enableClosureFlag && closureList?.length > 0) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closureList[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimit);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimit
        ? lockEditConfig
        : lockEditLastClosure.add(1, 'day');
    }

    if (rowData[key] < userLimitEdit) {
      return { isValid: false, helperText: t('validation:InvalidDate') };
    }

    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || currentDatetime.format('DD/MM/YYYY');
    const stTime = rowData.start_time || currentDatetime.format('HH:mm:ss');
    const endDate = rowData.end_date || currentDatetime.format('DD/MM/YYYY');
    const endTime = rowData.end_time || currentDatetime.format('HH:mm:ss');

    const startDateTimestamp = parseDateTimeToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateTimeToTimestamp(endDate, endTime);

    if (key.startsWith('end_')) {
      if (endDateTimestamp < startDateTimestamp) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (endDateTimestamp > currentDatetime.unix() * 1000) {
        return {
          isValid: false,
          helperText: translate('validation:FutureDateCantBeSelected'),
        };
      }
    }

    return { isValid: true };
  };

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'start_date'),
    },
    {
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'end_date'),
    },
    {
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
      validate: rowData => isDateEditable(rowData, 'start_time'),
    },
    {
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
      validate: rowData => isDateEditable(rowData, 'end_time'),
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
    },
    {
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
    },
    {
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
      customType: 'filterLookup',
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: scissorLiftGroups.filter(
        e => e.id_equipament === filters.equipmentType,
      ),
      customType: 'filterLookup',
    },
    {
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: scissorLiftEquips.filter(
        e => e.id_equip === filters.equipmentType,
      ),
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group) === String(rowData.equip_group_id),
    },
    {
      title: translate('common:Origin'),
      field: 'origin_element_id',
      lookup: elements,
      customType: 'filterLookup',
    },
    {
      title: translate('common:OriginPoint'),
      field: 'origin_subelement_id',
      lookup: subElements,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_element)
        === String(rowData.origin_element_id),
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      customType: 'filterLookup',
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group)
        === String(rowData.operator_group_id),
    },
    {
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypes.filter(r => r.id_equipment_type === 24),
      customType: 'filterLookup',
    },
    {
      title: translate('common:OperationActivity'),
      field: 'operation_activity_id',
      lookup: operationTypeActivities,
      customType: 'filterLookup',
    },
    {
      title: translate('common:TotalDimension'),
      field: 'total_dimension',
      customType: 'number',
    },
    {
      title: translate('common:IdleTime'),
      field: 'idle_time',
      customType: 'number',
    },
    {
      title: translate('common:OperationTime'),
      field: 'operation_time',
      customType: 'number',
    },
    {
      title: translate('common:CodeTime'),
      field: 'code_time',
      customType: 'number',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
    },
  ];

  const customEditHistoryColumns = [
    {
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
    },
    {
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      lookup: scissorLiftGroups.filter(e => e.id_equipament === filters.equipmentType),
    },
    {
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: scissorLiftEquips.filter(e => e.id_equip === filters.equipmentType),
    },
    {
      title: translate('common:Origin'),
      field: 'origin_element_id',
      lookup: elements,
    },
    {
      title: translate('common:OriginPoint'),
      field: 'origin_subelement_id',
      lookup: subElements,
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
    },
    {
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypes,
    },
    {
      title: translate('common:OperationActivity'),
      field: 'operation_activity_id',
      lookup: operationTypeActivities,
    },
    {
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
    },
  ];

  const filterToFormMultiFieldMap = {
    turn: 'turn_id',
    equipmentGroup: 'equip_group_id',
    equipment: 'equip_id',
    startOrigin: 'origin_element_id',
    startSubOrigin: 'origin_subelement_id',
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
    operatorType: 'operation_type_id',
  };

  const mappedFilterMultiData = Object.entries(filterToFormMultiFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValue = filters[filterKey];
      if (Array.isArray(filterValue) && filterValue.length === 1) {
        const [firstValue] = filterValue;
        acc[formField] = firstValue;
      } else if (filterValue !== undefined && filterValue !== null) {
        acc[formField] = filterValue;
      }
      return acc;
    },
    {},
  );

  // Verificar campos obrigatórios para desabilitar o botão de salvar
  const disableSaveAllChangesButton = scissorLiftList?.length ? scissorLiftList
    .filter(row => row.touched)
    .flatMap(row => (
      Object.entries(row).map(([key, value]) => columns.find(col => col.field === key)?.required && value === '')
    ))
    .includes(true) : false;

  const initialFormData = {
    ...filters,
    ...mappedFilterMultiData,
    start_date: formatDate(newMomentDate(), true),
    end_date: formatDate(newMomentDate(), true),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    duration: 0,
    team_id: 0,
    turn_id: 0,
    equip_group_id: 0,
    equip_id: 0,
    operator_group_id: 0,
    operator_id: 0,
    operation_type_id: 0,
    operation_activity_id: 0,
    total_dimension: 0,
    idle_time: 0,
    operation_time: 0,
    code_time: 0,
  };

  const removeUnnecessaryFields = (data) => {
    const result = { ...data };
    const fieldsToRemove = [
      'startDate',
      'endDate',
      'turn',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'material',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
    ];

    fieldsToRemove.forEach((field) => {
      delete result[field];
    });

    return result;
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'scissor_lift',
    pagination: scissorLiftPagination,
    searchAction: searchScissorLiftList,
  });

  const { page = 0, total = 0, loadingMore = false } = scissorLiftPagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        columns={applyDefaultSort(columns)}
        data={scissorLiftList}
        initialFormData={initialFormData}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          selection: true,
          search: false,
          cacheKey: 'scissor_lift_list',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          disableSaveButton: disableSaveAllChangesButton,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={(newItem) => {
          const createParams = {
            ...newItem,
            ...mappedFilterMultiData,
          };
          dispatch(createScissorLiftItem(createParams));
        }}
        onDiscard={() => dispatch(restoreOriginalScissorLift())}
        onApply={() => {
          const parsedData = scissorLiftList
            .filter(item => item.update_status)
            .map(removeUnnecessaryFields);

          const actionData = {
            data: parsedData,
            parameters: filters,
            saveLog: true,
            ogData: originalScissorLiftList,
          };
          dispatch(updateScissorLiftList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach(({ ...row }) => {
            dispatch(setScissorLiftValues(row, row));
          });
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deleteScissoLiftItem(e));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="scissorLift"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

ScissorLiftList.defaultProps = {};

export default withStyles(styles)(ScissorLiftList);
